import React, { useState, useEffect } from 'react';
import { 
  FaStar, FaCheck, FaTimes, FaLightbulb, 
  FaHistory, FaInfoCircle, FaClipboardCheck,
  FaUser, FaBuilding, FaCalendarAlt, FaUsers
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';

const STATUT_LABELS = {
  soumise: 'Soumise',
  en_evaluation: 'En évaluation',
  validee: 'Validée',
  rejetee: 'Rejetée',
};

const MesEvaluationsPage = () => {
  const api = useAxios();
  
  const [activeTab, setActiveTab] = useState('aEvaluer');
  const [aEvaluer, setAEvaluer] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modale évaluation
  const [showModal, setShowModal] = useState(false);
  const [innActive, setInnActive] = useState(null);
  const [criteres, setCriteres] = useState([]);
  const [notes, setNotes] = useState([]);
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Chargement des données
  useEffect(() => {
    fetchAEvaluer();
    fetchHistorique();
  }, []);
  
  const fetchAEvaluer = async () => {
    setLoading(true);
    try {
      const res = await api.get('/innovation/innovations/mes-evaluations/');
      setAEvaluer(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) {
      console.error(e);
      setAEvaluer([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchHistorique = async () => {
    try {
      const res = await api.get('/innovation/innovations/mes-evaluations-soumises/');
      setHistorique(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (e) {
      console.error(e);
      setHistorique([]);
    }
  };
  
  const ouvrirEvaluation = async (inn) => {
    try {
      const res = await api.get('/innovation/innovations/criteres/', {
        params: { organisation: inn.organisation_id },
      });
      const crits = (Array.isArray(res.data) ? res.data : []).filter(c => c.est_actif);
      
      setCriteres(crits);
      setInnActive(inn);
      setNotes(crits.map(c => ({ critere_id: c.id, note: '' })));
      setCommentaire('');
      setShowModal(true);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: "Impossible de charger les critères d'évaluation." });
    }
  };
  
  const setNote = (critere_id, val) =>
    setNotes(prev => prev.map(n => n.critere_id === critere_id ? { ...n, note: val } : n));
  
  const getNoteVal = (critere_id) =>
    notes.find(n => n.critere_id === critere_id)?.note ?? '';
  
  const handleSoumettre = async () => {
    const manquants = notes.filter(n => n.note === '' || n.note === null);
    if (manquants.length > 0) {
      return Swal.fire({ icon: 'error', title: 'Notes incomplètes', text: 'Veuillez noter tous les critères avant de soumettre.' });
    }
    
    const horsMax = notes.find(n => {
      const c = criteres.find(c => c.id === n.critere_id);
      return c && Number(n.note) > c.note_max;
    });
    if (horsMax) {
      const c = criteres.find(c => c.id === horsMax.critere_id);
      return Swal.fire({ icon: 'error', title: 'Note invalide', text: `La note pour "${c?.nom}" dépasse le maximum autorisé (${c?.note_max}).` });
    }
    
    const confirm = await Swal.fire({
      title: "Confirmer l'évaluation ?",
      text: "Cette action est définitive. Votre score ne pourra plus être modifié.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1B019B',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Oui, soumettre',
      cancelButtonText: 'Annuler',
    });
    if (!confirm.isConfirmed) return;
    
    setSubmitting(true);
    try {
      const res = await api.post(`/innovation/innovations/${innActive.id}/soumettre-evaluation/`, {
        notes: notes.map(n => ({ critere_id: n.critere_id, note: Number(n.note) })),
        commentaire,
      });
      Swal.fire({
        icon: 'success',
        title: 'Évaluation soumise !',
        html: `<div style="text-align:center">Score total : <strong style="color:#1B019B;font-size:22px">${res.data.score_total} pts</strong></div>`,
        timer: 2800,
        showConfirmButton: false,
      });
      setShowModal(false);
      fetchAEvaluer();
      fetchHistorique();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: e.response?.data?.error || e.message });
    } finally {
      setSubmitting(false);
    }
  };
  
  const scoreEstime = notes.reduce((s, n) => s + Number(n.note || 0), 0);
  const scoreMax = criteres.reduce((s, c) => s + c.note_max, 0);
  const pctEstime = scoreMax > 0 ? Math.min(100, Math.round((scoreEstime / scoreMax) * 100)) : 0;
  
  const enAttente = aEvaluer.filter(i => !i.deja_evalue);
  const dejaFait = aEvaluer.filter(i => i.deja_evalue);
  
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };
  
  const styles = {
    container: {
      padding: '30px',
      minHeight: '100vh',
      backgroundColor: '#f4f6f9',
      fontFamily: 'Segoe UI, sans-serif',
    },
    header: {
      color: '#1B019B',
      fontWeight: '600',
      fontSize: '1.5rem',
    },
    hr: {
      width: '80px',
      border: '2px solid #1B019B',
      opacity: '1',
      margin: '10px auto',
    },
    statsContainer: {
      display: 'flex',
      gap: '14px',
      marginBottom: '26px',
      flexWrap: 'wrap',
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '14px 22px',
      border: '1px solid #e2e8f0',
      minWidth: '148px',
      flex: '1 1 148px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    statLabel: {
      fontSize: '11.5px',
      fontWeight: '600',
      marginBottom: '5px',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: '700',
      lineHeight: 1,
    },
    tabContainer: {
      borderBottom: '2px solid #e2e8f0',
      marginBottom: '24px',
      display: 'flex',
      gap: '2px',
    },
    tab: {
      padding: '10px 20px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '13.5px',
      fontWeight: '500',
      color: '#64748b',
      background: 'none',
      borderBottom: '2.5px solid transparent',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.15s',
    },
    tabActive: {
      color: '#1B019B',
      borderBottomColor: '#1B019B',
    },
    card: {
      borderRadius: '15px',
      border: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      overflow: 'hidden',
    },
    innovationCard: {
      background: 'white',
      border: '1.5px solid #e2e8f0',
      borderRadius: '14px',
      padding: '22px',
      transition: 'all 0.2s',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '11.5px',
      fontWeight: '600',
    },
    btn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      padding: '9px 18px',
      border: 'none',
      borderRadius: '10px',
      fontFamily: 'inherit',
      fontSize: '13.5px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.18s ease',
    },
    btnPrimary: {
      background: '#1B019B',
      color: '#fff',
    },
    btnGhost: {
      background: '#f8fafc',
      color: '#334155',
      border: '1.5px solid #e2e8f0',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050,
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '540px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
    },
    modalHeader: {
      padding: '22px 26px 18px',
      borderBottom: '1px solid #e2e8f0',
    },
    modalBody: {
      padding: '22px 26px',
    },
    modalFooter: {
      padding: '16px 26px',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      background: '#f8fafc',
      borderRadius: '0 0 20px 20px',
    },
    noteRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '11px 14px',
      borderRadius: '10px',
      background: '#f8fafc',
      marginBottom: '8px',
      gap: '12px',
    },
    noteInput: {
      width: '82px',
      padding: '7px 10px',
      textAlign: 'center',
      border: '1.5px solid #e2e8f0',
      borderRadius: '8px',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '600',
      color: '#1B019B',
      outline: 'none',
      flexShrink: 0,
      background: 'white',
    },
    scoreBar: {
      height: '5px',
      borderRadius: '3px',
      background: '#e2e8f0',
      overflow: 'hidden',
    },
    scoreBarFill: {
      height: '100%',
      borderRadius: '3px',
      background: 'linear-gradient(90deg, #1B019B, #7c3aed)',
      transition: 'width 0.4s ease',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      padding: '56px 24px',
      color: '#64748b',
    },
    spinner: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      border: '3px solid #e2e8f0',
      borderTopColor: '#1B019B',
      animation: 'spin 0.7s linear infinite',
      margin: '50px auto',
    },
  };
  
  return (
    <div style={styles.container}>
      {/* Modal d'évaluation */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => !submitting && setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Saisir votre évaluation</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{innActive?.titre}</p>
                </div>
                <button onClick={() => !submitting && setShowModal(false)} style={{ ...styles.btn, ...styles.btnGhost, padding: '7px 10px' }}>
                  <FaTimes style={{ fontSize: 13 }} />
                </button>
              </div>
            </div>
            
            <div style={styles.modalBody}>
              {innActive && (
                <div style={{ background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: 12, padding: '14px 16px', marginBottom: 22 }}>
                  <div style={{ fontSize: 11.5, color: '#1B019B', fontWeight: 600, marginBottom: 5 }}>Innovation à évaluer</div>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{innActive.titre}</div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#64748b' }}>
                    {innActive.domaine && <span>Domaine : <strong>{innActive.domaine}</strong></span>}
                    <span>Auteur : <strong>{`${innActive.auteur_prenom || ''} ${innActive.auteur_nom || ''}`.trim() || '—'}</strong></span>
                  </div>
                </div>
              )}
              
              {criteres.length === 0 ? (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 10 }}>
                  <FaInfoCircle style={{ color: '#b45309' }} />
                  <span style={{ fontSize: 14, color: '#b45309' }}>Aucun critère d'évaluation actif configuré. Contactez votre responsable RH.</span>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#334155', marginBottom: 12 }}>Notes par critère <span style={{ color: '#e11d48' }}>*</span></div>
                  
                  {criteres.map(c => {
                    const val = getNoteVal(c.id);
                    const isOver = val !== '' && Number(val) > c.note_max;
                    return (
                      <div key={c.id} style={styles.noteRow}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nom}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>sur {c.note_max} points</div>
                        </div>
                        <input
                          type="number"
                          min="0"
                          max={c.note_max}
                          step="0.5"
                          style={{
                            ...styles.noteInput,
                            ...(isOver && { borderColor: '#e11d48', color: '#e11d48' })
                          }}
                          value={val}
                          onChange={e => setNote(c.id, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    );
                  })}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#ede9fe', borderRadius: 10, marginTop: 6 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1B019B', marginBottom: 6 }}>Score estimé</div>
                      <div style={styles.scoreBar}>
                        <div style={{ ...styles.scoreBarFill, width: `${pctEstime}%` }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>{pctEstime}%</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 26, fontWeight: 700, color: '#1B019B' }}>{scoreEstime}</span>
                      <span style={{ fontSize: 13, color: '#64748b' }}> / {scoreMax}</span>
                    </div>
                  </div>
                </>
              )}
              
              <div style={{ marginTop: 18 }}>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Commentaire <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span>
                </label>
                <textarea
                  style={{ width: '100%', padding: '9px 13px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical' }}
                  rows={3}
                  placeholder="Partagez vos observations sur cette innovation..."
                  value={commentaire}
                  onChange={e => setCommentaire(e.target.value)}
                />
              </div>
              
              <div style={{ marginTop: 14, display: 'flex', gap: 9, padding: '11px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                <FaInfoCircle style={{ color: '#ea580c', flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 13, color: '#9a3412' }}>Une fois soumise, votre évaluation est <strong>définitive</strong> et ne pourra plus être modifiée.</span>
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              <button onClick={() => setShowModal(false)} disabled={submitting} style={{ ...styles.btn, ...styles.btnGhost }}>Annuler</button>
              <button onClick={handleSoumettre} disabled={submitting} style={{ ...styles.btn, ...styles.btnPrimary }}>
                {submitting ? 'Envoi en cours...' : <><FaCheck /> Soumettre mon évaluation</>}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="text-center mb-3">
        <h3 style={styles.header}>Mes évaluations d'innovation</h3>
        <hr style={styles.hr} />
      </div>
      
      {/* Cartes stats */}
      <div style={styles.statsContainer}>
        <div style={{ ...styles.statCard, background: '#ede9fe' }}>
          <div style={{ ...styles.statLabel, color: '#1B019B' }}>En attente</div>
          <div style={{ ...styles.statValue, color: '#1B019B' }}>{enAttente.length}</div>
        </div>
        <div style={{ ...styles.statCard, background: '#ecfdf5' }}>
          <div style={{ ...styles.statLabel, color: '#059669' }}>Déjà évaluées</div>
          <div style={{ ...styles.statValue, color: '#059669' }}>{dejaFait.length}</div>
        </div>
        <div style={{ ...styles.statCard, background: '#f8fafc' }}>
          <div style={{ ...styles.statLabel, color: '#334155' }}>Total reçues</div>
          <div style={{ ...styles.statValue, color: '#334155' }}>{aEvaluer.length}</div>
        </div>
        <div style={{ ...styles.statCard, background: '#fffbeb' }}>
          <div style={{ ...styles.statLabel, color: '#d97706' }}>Scores soumis</div>
          <div style={{ ...styles.statValue, color: '#d97706' }}>{historique.length}</div>
        </div>
      </div>
      
      {/* Onglets */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('aEvaluer')}
          style={{ ...styles.tab, ...(activeTab === 'aEvaluer' && styles.tabActive) }}
        >
          <FaLightbulb /> À évaluer
          {enAttente.length > 0 && (
            <span style={{ background: '#1B019B', color: '#fff', borderRadius: '20px', padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{enAttente.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('historique')}
          style={{ ...styles.tab, ...(activeTab === 'historique' && styles.tabActive) }}
        >
          <FaHistory /> Mes scores
        </button>
      </div>
      
      {/* Contenu */}
      {activeTab === 'aEvaluer' && (
        <>
          {loading ? (
            <div style={styles.spinner} />
          ) : aEvaluer.length === 0 ? (
            <div style={styles.card}>
              <div style={styles.emptyState}>
                <FaLightbulb style={{ fontSize: 38, color: '#cbd5e1' }} />
                <p style={{ margin: 0, fontSize: 15 }}>Aucune innovation à évaluer pour le moment</p>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Votre responsable RH vous désignera lorsqu'une évaluation vous sera assignée</p>
              </div>
            </div>
          ) : (
            <>
              {enAttente.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: '#334155' }}>En attente</h3>
                    <span style={{ background: '#ede9fe', color: '#1B019B', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{enAttente.length}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 16 }}>
                    {enAttente.map(inn => (
                      <div key={inn.id} style={styles.innovationCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{inn.titre}</div>
                            {inn.domaine && (
                              <span style={{ fontSize: 12, background: '#ede9fe', color: '#1B019B', padding: '2px 9px', borderRadius: 20 }}>{inn.domaine}</span>
                            )}
                          </div>
                          <span style={{ background: '#fffbeb', color: '#b45309', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>À évaluer</span>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Auteur</div>
                            <div style={{ fontSize: 13 }}>{`${inn.auteur_prenom || ''} ${inn.auteur_nom || ''}`.trim() || '—'}</div>
                          </div>
                          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Organisation</div>
                            <div style={{ fontSize: 13 }}>{inn.organisation || '—'}</div>
                          </div>
                          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Date soumission</div>
                            <div style={{ fontSize: 13 }}>{formatDate(inn.date_soumission)}</div>
                          </div>
                          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Évaluateurs</div>
                            <div style={{ fontSize: 13 }}>{inn.nombre_evaluateurs || 0}</div>
                          </div>
                        </div>
                        
                        <button onClick={() => ouvrirEvaluation(inn)} style={{ ...styles.btn, ...styles.btnPrimary, width: '100%', justifyContent: 'center', padding: '10px' }}>
                          <FaStar /> Évaluer cette innovation
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {dejaFait.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: '#64748b' }}>Déjà évaluées</h3>
                    <span style={{ background: '#ecfdf5', color: '#059669', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{dejaFait.length}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 16 }}>
                    {dejaFait.map(inn => (
                      <div key={inn.id} style={{ ...styles.innovationCard, opacity: 0.75, background: '#f8fafc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{inn.titre}</div>
                            {inn.domaine && (
                              <span style={{ fontSize: 12, background: '#ede9fe', color: '#1B019B', padding: '2px 9px', borderRadius: 20 }}>{inn.domaine}</span>
                            )}
                          </div>
                          <span style={{ background: '#ecfdf5', color: '#059669', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FaCheck style={{ fontSize: 10 }} /> Soumise
                          </span>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                          <div style={{ background: 'white', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Auteur</div>
                            <div style={{ fontSize: 13 }}>{`${inn.auteur_prenom || ''} ${inn.auteur_nom || ''}`.trim() || '—'}</div>
                          </div>
                          <div style={{ background: 'white', borderRadius: 8, padding: '8px 11px' }}>
                            <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 3 }}>Organisation</div>
                            <div style={{ fontSize: 13 }}>{inn.organisation || '—'}</div>
                          </div>
                        </div>
                        
                        <div style={{ textAlign: 'center', fontSize: 13, color: '#059669', padding: '6px 0 2px', fontWeight: 500 }}>
                          <FaClipboardCheck style={{ marginRight: 6 }} />
                          Votre évaluation a bien été enregistrée
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      
      {activeTab === 'historique' && (
        <div style={styles.card}>
          {historique.length === 0 ? (
            <div style={styles.emptyState}>
              <FaHistory style={{ fontSize: 38, color: '#cbd5e1' }} />
              <p style={{ margin: 0, fontSize: 15 }}>Aucun score soumis pour le moment</p>
            </div>
          ) : (
            historique.map((s, i) => {
              const totalMax = s.notes.reduce((acc, n) => acc + n.note_max, 0);
              const pct = totalMax > 0 ? Math.round((s.score_total / totalMax) * 100) : 0;
              return (
                <div key={s.id} style={{ padding: '20px 24px', borderBottom: i < historique.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 15 }}>{s.innovation_titre}</strong>
                        <span style={{ ...styles.badge, background: s.innovation_statut === 'validee' ? '#ecfdf5' : '#eff6ff', color: s.innovation_statut === 'validee' ? '#059669' : '#2563eb' }}>
                          {STATUT_LABELS[s.innovation_statut] || s.innovation_statut}
                        </span>
                      </div>
                      <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 12 }}>Soumis le {s.date}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {s.notes.map((n, j) => (
                          <span key={j} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 10px', fontSize: 12.5 }}>
                            {n.critere_nom} : <strong style={{ color: '#1B019B' }}>{n.note}/{n.note_max}</strong>
                          </span>
                        ))}
                      </div>
                      {s.commentaire && (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>"{s.commentaire}"</p>
                      )}
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 90 }}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: '#1B019B', lineHeight: 1 }}>{s.score_total ?? '—'}</div>
                      <div style={{ fontSize: 11.5, color: '#94a3b8' }}>/ {totalMax} pts</div>
                      {s.score_total != null && totalMax > 0 && (
                        <>
                          <div style={styles.scoreBar}>
                            <div style={{ ...styles.scoreBarFill, width: `${pct}%` }} />
                          </div>
                          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>{pct}%</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MesEvaluationsPage;